import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useMemo } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase.js";
import Cookies from "js-cookie";
import { Toast } from "../components_root/ToastContext.jsx";

import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

export default function Last_week_cash_book() {
    const [ledgerAccounts, setLedgerAccounts] = useState([]);
    const [chartLabels, setChartLabels] = useState([]);
    const [chartAmounts, setChartAmounts] = useState([]);
    const servicetablename = "accountingfinancedb";
    const { Toastshow } = Toast();
    const navigate = useNavigate();

    // ---------------------------------------------------------
    // FIRESTORE SNAPSHOT
    // ---------------------------------------------------------
    useEffect(() => {
        const companyId = Cookies.get("select_company_uid");
        if (!companyId) {
            Toastshow("Company not selected");
            return;
        }

        const ref = collection(
            db,
            servicetablename,
            companyId,
            "cash_books_accounting"
        );

        const unsub = onSnapshot(ref, (snapshot) => {
            const rows = snapshot.docs.map((doc) => {
                const d = doc.data();
                return {
                    id: doc.id,
                    ...d,
                    _date: d.date_time?.toDate
                        ? d.date_time.toDate()
                        : new Date(d.date_time),
                };
            });

            setLedgerAccounts(rows);
        });

        return () => unsub();
    }, []);

    // ---------------------------------------------------------
    // LAST 7 DAYS DATA
    // ---------------------------------------------------------
    useEffect(() => {
        if (!ledgerAccounts.length) return;

        const today = new Date();
        const labels = [];
        const amounts = [];

        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(today.getDate() - i);

            labels.push(
                d.toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                })
            );

            const total = ledgerAccounts
                .filter(
                    (v) => v._date.toDateString() === d.toDateString()
                )
                .reduce(
                    (sum, v) =>
                        sum +
                        Number(v.credit || 0) +
                        Number(v.debit || 0),
                    0
                );

            amounts.push(total);
        }

        setChartLabels(labels);
        setChartAmounts(amounts);
    }, [ledgerAccounts]);

    const primaryColor =
        getComputedStyle(document.documentElement)
            .getPropertyValue("--primary-color")
            .trim() || "#4f46e5";
    const primarylightColor =
        getComputedStyle(document.documentElement)
            .getPropertyValue("--blue-light-color")
            .trim() || "#4f46e5";
    // ---------------------------------------------------------
    // LINE GRAPH DATA
    // ---------------------------------------------------------
    const chartData = useMemo(
        () => ({
            labels: chartLabels,
            datasets: [
                {
                    label: "Total Amount",
                    data: chartAmounts,
                    borderColor: primaryColor,
                    backgroundColor: `${primaryColor}33`,
                    fill: true,
                    tension: 0.45,
                    borderWidth: 1,
                    pointRadius: 4,
                    pointHoverRadius: 10,
                    pointBackgroundColor: primaryColor,
                },
            ],
        }),
        [chartLabels, chartAmounts, primaryColor]
    );

    // ---------------------------------------------------------
    // GRAPH OPTIONS
    // ---------------------------------------------------------
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) =>
                        `₹ ${ctx.parsed.y.toLocaleString("en-IN")}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { size: 10 } },
            },
            y: {
                grid: { color: "rgba(0,0,0,0.06)" },
                ticks: {
                    callback: (v) => `₹ ${v}`,
                    font: { size: 10 },
                },
            },
        },
    };

    return (
        <div className="p-11">
              <div className="b-bottom text-h2 p-1">
                Cash Book – Last 7 Days
            </div>

            <div style={{ height: "220px" }}>
                <Line data={chartData} options={chartOptions} />
            </div>
        </div>
    );
}
